import React, { useState, useEffect } from "react";
import api from "../Service/api";
import "./EmployeeSalary.css";

export default function EmployeeSalaryManagement() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("salary");
  const [pendingAdvance, setPendingAdvance] = useState(0);

  const getTodayDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to convert date string to LocalDateTime without timezone offset
  const formatDateToLocalDateTime = (dateString) => {
    if (!dateString) return null;
    // Format: YYYY-MM-DDThh:mm:ss (local time, no timezone)
    return `${dateString}T00:00:00`;
  };

  const resetPage = () => {
    setSelectedEmployee(null);
    setPendingAdvance(0);
    setTotalPayable(0);
    setActiveTab("salary");

    setSalaryForm({
      bonus: 0,
      overTime: 0,
      deduction: 0,
      advanceAdjustment: 0,
      salaryType: "MONTHLY",
      salaryDate: getTodayDate(),
    });

    setAdvanceForm({
      amount: 0,
      remarks: "",
      paidOn: getTodayDate(),
    });
  };

  const [salaryForm, setSalaryForm] = useState({
    bonus: 0,
    overTime: 0,
    deduction: 0,
    advanceAdjustment: 0,
    salaryType: "MONTHLY",
    salaryDate: getTodayDate(),
  });

  const [advanceForm, setAdvanceForm] = useState({
    amount: 0,
    remarks: "",
    paidOn: getTodayDate(),
  });

  const [totalPayable, setTotalPayable] = useState(0);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/api/employees");
      setEmployees(res.data.additionalDetail || []);
    } catch (err) {
      console.error(err);
      alert("Error fetching employees");
    }
  };

  const handleEmployeeChange = async (e) => {
    const empId = e.target.value;
    const emp = employees.find((emp) => emp.id.toString() === empId);
    setSelectedEmployee(emp);

    if (!emp) return;

    try {
      const res = await api.get("/api/salary/info", {
        params: {
          name: emp.name,
          designation: emp.designation,
        },
      });

      if (res.data.code === 1) {
        const data = res.data.additionalDetail;
        setPendingAdvance(data.pendingAdvance || 0);
        setSalaryForm({
          bonus: 0,
          overTime: 0,
          deduction: 0,
          advanceAdjustment: data.pendingAdvance || 0,
          salaryType: "MONTHLY",
          salaryDate: getTodayDate(),
        });
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching employee salary info");
    }
  };

  const handleSalaryChange = (e) => {
    const { name, value } = e.target;
    setSalaryForm((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  useEffect(() => {
    if (!selectedEmployee) return;
    const total =
      (selectedEmployee.salary || 0) +
      (salaryForm.bonus || 0) +
      (salaryForm.overTime || 0) -
      (salaryForm.deduction || 0) -
      (salaryForm.advanceAdjustment || 0);
    setTotalPayable(total);
  }, [salaryForm, selectedEmployee]);

  const handleAdvanceChange = (e) => {
    const { name, value } = e.target;
    setAdvanceForm((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) || 0 : value,
    }));
  };

  const submitSalary = async () => {
    if (!selectedEmployee) return alert("Select an employee first");

    try {
      const payload = {
        employeeId: selectedEmployee.id,
        bonus: salaryForm.bonus,
        overTime: salaryForm.overTime,
        deduction: salaryForm.deduction,
        advanceAdjustment: salaryForm.advanceAdjustment,
        salaryType: salaryForm.salaryType,
        salaryDate: salaryForm.salaryDate,
      };

      const res = await api.post("/api/salary/pay", payload);

      if (res.data.code === 1) {
        alert("Salary paid successfully!");
        resetPage();
      } else {
        alert(res.data.additionalDetail);
      }
    } catch (err) {
      console.error(err);
      alert("Error paying salary");
    }
  };

  // Submit advance with correct date handling
  const submitAdvance = async () => {
    if (!selectedEmployee) return alert("Select an employee first");
    
    if (advanceForm.amount <= 0) {
      alert("Please enter a valid advance amount");
      return;
    }

    if (!advanceForm.paidOn) {
      alert("Please select a date for the advance");
      return;
    }

    try {
      // Format the date to local datetime string (YYYY-MM-DDThh:mm:ss)
      // This preserves the exact date without timezone conversion
      const localDateTime = formatDateToLocalDateTime(advanceForm.paidOn);
      
      const payload = {
        employeeId: selectedEmployee.id,
        amount: advanceForm.amount,
        remarks: advanceForm.remarks,
        paidOn: localDateTime, // Send as local datetime string
        salaryType: "ADVANCE",
      };

      console.log("Selected date:", advanceForm.paidOn);
      console.log("Sending as local datetime:", localDateTime);
      console.log("Full payload:", payload);

      const res = await api.post("/api/salary/advance", payload);
      
      if (res.data.code === 1) {
        alert(res.data.message || "Advance given successfully!");
        setAdvanceForm({ 
          amount: 0, 
          remarks: "",
          paidOn: getTodayDate() 
        });
        // Refresh employee info to update pending advance
        if (selectedEmployee) {
          const empRes = await api.get("/api/salary/info", {
            params: {
              name: selectedEmployee.name,
              designation: selectedEmployee.designation,
            },
          });
          if (empRes.data.code === 1) {
            setPendingAdvance(empRes.data.additionalDetail.pendingAdvance || 0);
          }
        }
        resetPage();
      } else {
        alert(res.data.message || "Failed to give advance");
      }
    } catch (err) {
      console.error("Error giving advance:", err);
      alert(err.response?.data?.message || "Error giving advance");
    }
  };

  return (
    <div className="container salary-payment">
      <h1>💰 Employee Salary Management</h1>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === "salary" ? "active-tab" : ""}
          onClick={() => setActiveTab("salary")}
        >
          Salary Payment
        </button>
        <button
          className={activeTab === "advance" ? "active-tab" : ""}
          onClick={() => setActiveTab("advance")}
        >
          Advance Payment
        </button>
      </div>

      {/* Employee selection for both forms */}
      <div className="form-card">
        <div className="form-row">
          <label>Employee Name & Designation</label>
          <select onChange={handleEmployeeChange} value={selectedEmployee?.id || ""}>
            <option value="">Select Employee</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name} - {emp.designation}
              </option>
            ))}
          </select>
        </div>

        {selectedEmployee && activeTab === "salary" && (
          <>
            <div className="form-row">
              <label>Base Salary</label>
              <input
                type="number"
                value={selectedEmployee.salary}
                readOnly
                style={{ color: "black" }}
              />
            </div>

            <div className="form-row">
              <label>Bonus</label>
              <input
                type="number"
                name="bonus"
                value={salaryForm.bonus}
                onChange={handleSalaryChange}
              />
            </div>

            <div className="form-row">
              <label>Overtime</label>
              <input
                type="number"
                name="overTime"
                value={salaryForm.overTime}
                onChange={handleSalaryChange}
              />
            </div>

            <div className="form-row">
              <label>Deduction</label>
              <input
                type="number"
                name="deduction"
                value={salaryForm.deduction}
                onChange={handleSalaryChange}
              />
            </div>

            <div className="form-row">
              <label>Advance Adjustment</label>
              <input
                type="number"
                name="advanceAdjustment"
                value={salaryForm.advanceAdjustment}
                onChange={handleSalaryChange}
              />
              {pendingAdvance > 0 && (
                <small className="advance-info">
                  Pending Advance: {pendingAdvance}
                </small>
              )}
            </div>

            <div className="form-row">
              <label>Salary Type</label>
              <select
                name="salaryType"
                value={salaryForm.salaryType}
                onChange={(e) =>
                  setSalaryForm({ ...salaryForm, salaryType: e.target.value })
                }
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>

            <div className="form-row">
              <label>Salary Month</label>
              <input
                type="date"
                name="salaryDate"
                value={salaryForm.salaryDate}
                onChange={(e) =>
                  setSalaryForm({ ...salaryForm, salaryDate: e.target.value })
                }
              />
            </div>

            <div className="form-row">
              <label>Total Payable</label>
              <input type="number" value={totalPayable} readOnly style={{ color: "black" }} />
            </div>

            <div className="form-actions">
              <button className="submit-btn" onClick={submitSalary}>
                Pay Salary
              </button>
            </div>
          </>
        )}

        {selectedEmployee && activeTab === "advance" && (
          <>
            <div className="form-row">
              <label>Pending Advance</label>
              <input
                type="text"
                value={`${pendingAdvance}`}
                readOnly
                style={{ color: "black", backgroundColor: "#f5f5f5" }}
              />
            </div>

            <div className="form-row">
              <label>Advance Amount *</label>
              <input
                type="number"
                name="amount"
                value={advanceForm.amount}
                onChange={handleAdvanceChange}
                placeholder="Enter advance amount"
                min="0"
                step="100"
              />
            </div>

            <div className="form-row">
              <label>Advance Date *</label>
              <input
                type="date"
                name="paidOn"
                value={advanceForm.paidOn}
                onChange={handleAdvanceChange}
              />
              <small>Select the date when advance was given</small>
            </div>

            <div className="form-row">
              <label>Remarks (Optional)</label>
              <textarea
                name="remarks"
                value={advanceForm.remarks}
                onChange={handleAdvanceChange}
                placeholder="Add any remarks or reason for advance"
                rows="3"
              />
            </div>

            {advanceForm.amount > 0 && (
              <div className="advance-summary">
                <h4>Advance Summary</h4>
                <div className="summary-item">
                  <span>Current Advance Amount:</span>
                  <strong>{advanceForm.amount}</strong>
                </div>
                <div className="summary-item">
                  <span>Pending Advance After:</span>
                  <strong>{pendingAdvance + advanceForm.amount}</strong>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button className="submit-btn" onClick={submitAdvance}>
                Give Advance
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}